import React, { useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { PAYMENT_HISTORY } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import './Page.css';
import './Payments.css';

function formatIsoDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function isLikelyPhone(value) {
  const cleaned = (value || '').replace(/\s/g, '');
  return /^[\d+\-()]+$/.test(cleaned) && cleaned.length >= 10;
}

function formatAmount(amount, currency) {
  if (amount == null || amount === '') return '—';
  const curr = (currency || 'INR').toUpperCase();
  if (curr === 'INR') return `₹${Number(amount).toLocaleString('en-IN')}`;
  return `${curr} ${Number(amount).toLocaleString()}`;
}

function PaymentHistory() {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    const value = (input || '').trim();
    if (!value) {
      setError('Enter a user ID or phone number.');
      setHistory(null);
      return;
    }
    setError('');
    setHistory(null);
    setLoading(true);
    const params = isLikelyPhone(value) ? { phone_number: value } : { user_id: value };
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(apiUrl(PAYMENT_HISTORY.pathWithQuery(params)), {
        method: PAYMENT_HISTORY.method,
        headers,
      });
      const data = await res.json().catch(() => ({}));
      const payload = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
      if (res.ok) {
        setHistory(payload);
      } else {
        setError(getApiErrorMessage(data, res.status));
        setHistory([]);
      }
    } catch (err) {
      setError(err.message || 'Unable to connect. Please try again.');
      setHistory(null);
    } finally {
      setLoading(false);
    }
  };

  const historySorted = Array.isArray(history)
    ? [...history].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    : [];

  return (
    <div className="page payments-page">
      <div className="payments-header">
        <h3>View payment history</h3>
        <p className="payments-subtitle">Search payment history by user ID or phone number.</p>
      </div>

      <form onSubmit={handleSearch} className="payments-search-card">
        <div className="payments-search-row">
          <label htmlFor="payment-history-search-input" className="payments-search-label">
            User ID or phone number <span className="label-required" aria-hidden="true">*</span>
          </label>
          <div className="payments-search-input-wrap">
            <input
              id="payment-history-search-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. 796be5d4-4832-497a-be13-a3bd5a044cbb or +91-7894332058"
              disabled={loading}
              autoComplete="off"
              className="payments-search-input"
            />
            <button type="submit" className="payments-search-btn" disabled={loading}>
              <FiSearch className="payments-search-btn-icon" aria-hidden />
              {loading ? 'Searching…' : 'Search payment history'}
            </button>
          </div>
        </div>
        {error && <p className="payments-error" role="alert">{error}</p>}
      </form>

      {history !== null && (
        <div className="payments-history-card">
          <h4 className="payments-result-title">Payment history</h4>
          {historySorted.length === 0 ? (
            <p className="payments-history-empty">No payment history found for this user.</p>
          ) : (
            <div className="payments-history-table-wrap">
              <table className="payments-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Currency</th>
                    <th>Payment ID</th>
                    <th>Order ID</th>
                    <th>Subscription ID</th>
                  </tr>
                </thead>
                <tbody>
                  {historySorted.map((row, idx) => (
                    <tr key={row.razorpay_payment_id || idx}>
                      <td className="payments-history-date">{formatIsoDate(row.created_at)}</td>
                      <td>
                        <span className={`payments-history-pill payments-history-type-${(row.type || '').toLowerCase()}`}>
                          {row.type || '—'}
                        </span>
                      </td>
                      <td>
                        <span className={`payments-history-pill payments-history-status-${(row.status || '').toLowerCase()}`}>
                          {row.status || '—'}
                        </span>
                      </td>
                      <td className="payments-history-amount">{formatAmount(row.amount, row.currency)}</td>
                      <td>{row.currency || '—'}</td>
                      <td className="payments-history-id" title={row.razorpay_payment_id}>{row.razorpay_payment_id || '—'}</td>
                      <td className="payments-history-id" title={row.razorpay_order_id}>{row.razorpay_order_id || '—'}</td>
                      <td className="payments-history-id" title={row.razorpay_subscription_id}>{row.razorpay_subscription_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PaymentHistory;
